// AcademicPartnerships.js
import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, GraduationCap, Award } from 'lucide-react';

const AcademicPartnerships = () => {
  const countries = [
    { name: 'United States', image: 'usa.jpg' },
    { name: 'Canada', image: 'canada.jpg' },
    { name: 'United Kingdom', image: 'uk.jpg' },
    { name: 'Australia', image: 'australia.jpg' },
    { name: 'Europe', image: 'europe.jpg' }
  ];

  const highlights = [
    {
      icon: GraduationCap,
      stat: 'Direct',
      label: 'Institutional Links',
      description: 'In the countries our candidates apply to'
    },
    {
      icon: Award,
      stat: 'Reduced',
      label: 'Tuition Where Offered',
      description: 'Open to candidates who meet the criteria'
    },
    {
      icon: MapPin,
      stat: 'Five',
      label: 'Study Regions',
      description: 'Where our partner schools are based'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Working With Academic Institutions
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-3xl mx-auto">
            Ivipass has kept up relationships with universities and colleges in the countries our candidates apply to, and those links have been maintained over a long stretch. They let us put an application forward with more context behind it, and they let us ask about scholarships or discounted tuition for the candidates already moving through our pipeline.
          </p>
        </motion.div>

        {/* Built on Trust and Experience */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-surface p-8 rounded-lg mb-12 max-w-4xl mx-auto"
        >
          <h3 className="text-xl font-semibold text-primary text-center mb-4">
            How the Network Was Built
          </h3>
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            A partner list is only worth as much as the relationships sitting behind it. Ours came from returning to the same institutions and dealing with them straight each time. For a candidate, that shows up as a shorter route into an application and access to openings that a cold submission would never reach.
          </p>
        </motion.div>

        {/* Stats Highlights */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {highlights.map((highlight, index) => {
            const Icon = highlight.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-surface rounded-full mb-4">
                  <Icon size={28} className="text-primary" />
                </div>
                <div className="text-4xl font-light text-primary mb-2">
                  {highlight.stat}
                </div>
                <div className="text-base font-semibold text-gray-700 mb-1">
                  {highlight.label}
                </div>
                <div className="text-sm text-gray-600 font-light">
                  {highlight.description}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Countries Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3 className="text-xl font-semibold text-primary text-center mb-8">
            Where You Can Study
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {countries.map((country, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 + (index * 0.05) }}
                className="bg-surface p-6 text-center hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-secondary/20"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full overflow-hidden shadow-md">
                  <img 
                    src={require(`../../assets/countries/${country.image}`)} 
                    alt={country.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-sm font-normal text-gray-700">
                  {country.name}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Scholarship Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 bg-surface p-8 rounded-lg"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-lg font-semibold text-primary mb-4">
              Scholarships and Discounted Tuition
            </h3>
            <p className="text-gray-600 text-sm font-light leading-relaxed mb-4">
              Some institutions set aside scholarship funding or lower their fees for particular applicants. Whether you are put forward for one, and how far a fee is reduced, comes down to your academic record, the programme you have chosen, and what the institution still has available.
            </p>
            <p className="text-gray-600 text-sm font-light leading-relaxed">
              Awards of this kind are limited and competed for, so nothing here is promised. When we recommend you for something we explain why, and the reason is always that you meet the criteria.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AcademicPartnerships;
